import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtEUR } from "@/lib/constants";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { FileSignature, Download, ShieldAlert, ShieldCheck, TrendingUp } from "lucide-react";

const STATUS_STYLE = {
    pending_signature: { label: "À signer", cls: "pill pill-gold" },
    signed: { label: "Signé", cls: "pill pill-green" },
    cancelled: { label: "Annulé", cls: "pill pill-red" },
    blocked: { label: "Bloqué", cls: "pill pill-red" },
};


export default function CandidateContracts({ firstName }) {
    const [turnover, setTurnover] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [activePad, setActivePad] = useState(null);
    const [signerName, setSignerName] = useState(firstName || "");

    const refresh = async () => {
        try {
            const [t, list] = await Promise.all([
                api.get("/candidates/me/turnover"),
                api.get("/contracts"),
            ]);
            setTurnover(t.data);
            setContracts(list.data);
        } catch (_) {}
    };

    useEffect(() => {
        refresh();
    }, []);

    const sign = async (contractId, dataUrl) => {
        if (!signerName || signerName.trim().length < 3) {
            toast.error("Veuillez saisir votre nom et prénom pour signer");
            return;
        }
        try {
            await api.post(`/contracts/${contractId}/sign`, {
                signature_data_url: dataUrl,
                signed_by_name: signerName.trim(),
            });
            toast.success("Contrat signé — PDF généré et envoyé par e-mail");
            setActivePad(null);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de la signature");
        }
    };

    const download = async (contractId, num) => {
        try {
            const resp = await api.get(`/contracts/${contractId}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `contrat-${num}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (_) {
            toast.error("PDF indisponible");
        }
    };

    const t = turnover || {};
    const cumul = t.cumulative_signed || 0;
    const threshold = t.threshold || 5000;
    const pct = Math.min((cumul / threshold) * 100, 100);
    const isLocked = t.is_locked;

    return (
        <div className="space-y-6" data-testid="candidate-contracts">
            {/* Turnover / lock status */}
            <div className={`sd-card p-6 border-2 ${isLocked ? "border-red-300" : "border-transparent"}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="overline mb-1 flex items-center gap-2">
                            <TrendingUp size={12} className="text-[#F5A623]" />
                            Chiffre d'affaires signé — auto-entrepreneur
                        </div>
                        <div className="kpi-value">{fmtEUR(cumul)}</div>
                        <div className="text-xs text-slate-500 mt-1">
                            sur un seuil de <b>{fmtEUR(threshold)}</b> avant obligation
                            d'attestation de vigilance
                        </div>
                    </div>
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-200 text-sm font-semibold">
                            <ShieldAlert size={16} /> Signature bloquée
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-200 text-sm font-semibold">
                            <ShieldCheck size={16} />
                            {t.has_valid_vigilance ? "Attestation valide" : "Signature autorisée"}
                        </div>
                    )}
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                    <div
                        className={`h-full transition-all duration-500 ${
                            isLocked ? "bg-red-500" :
                            pct > 70 ? "bg-[#F5A623]" : "bg-green-500"
                        }`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                {isLocked && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                        <b>Vous avez atteint le seuil de {fmtEUR(threshold)}.</b> Pour
                        continuer à signer de nouveaux contrats, téléversez une{" "}
                        <b>attestation de vigilance</b> valide dans l'onglet
                        <i> Mes documents</i>. Le verrou sera levé automatiquement dès
                        que l'administrateur l'aura vérifiée.
                    </div>
                )}
            </div>

            {/* Contracts list */}
            <div className="sd-card p-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <div className="overline mb-1">Mes contrats</div>
                        <h3 className="font-display text-xl">Historique & signature</h3>
                    </div>
                </div>

                {contracts.length === 0 && (
                    <div className="text-sm text-slate-400 text-center py-8">
                        Aucun contrat pour l'instant. Dès qu'un client vous sélectionne
                        pour une mission, un contrat sera généré ici.
                    </div>
                )}

                <div className="space-y-4">
                    {contracts.map((c) => {
                        const meta = STATUS_STYLE[c.status] || STATUS_STYLE.pending_signature;
                        return (
                            <div
                                key={c.id}
                                className="border border-slate-200 rounded-xl p-4 bg-white"
                                data-testid={`contract-row-${c.id}`}
                            >
                                <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
                                    <div>
                                        <div className="font-semibold">
                                            {c.mission_snapshot?.title || "Mission"}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            <b>{c.contract_number}</b> · {c.mission_snapshot?.date} · {c.mission_snapshot?.city}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={meta.cls}>{meta.label}</span>
                                        {c.status === "signed" && (
                                            <button
                                                onClick={() => download(c.id, c.contract_number)}
                                                className="btn-ghost-navy text-xs"
                                                data-testid={`download-contract-${c.id}`}
                                            >
                                                <Download size={12} className="inline -mt-0.5 mr-1" />
                                                PDF
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-sm mb-3 bg-slate-50 rounded-lg p-3">
                                    <div>
                                        <div className="text-[10px] text-slate-500 uppercase font-semibold tracking-widest">Rémunération</div>
                                        <div className="font-semibold">{fmtEUR(c.professional_gross)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-slate-500 uppercase font-semibold tracking-widest">Heures</div>
                                        <div className="font-semibold">{c.hours} h</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] text-slate-500 uppercase font-semibold tracking-widest">Taux</div>
                                        <div className="font-semibold">{fmtEUR(c.hourly_rate)}/h</div>
                                    </div>
                                </div>
                                {c.status === "pending_signature" && (
                                    <div>
                                        {activePad === c.id ? (
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold mb-1 text-slate-600">
                                                        Votre nom et prénom
                                                    </label>
                                                    <input
                                                        value={signerName}
                                                        onChange={(e) => setSignerName(e.target.value)}
                                                        placeholder="Ex : Marie Dupont"
                                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                                                        data-testid={`signer-name-input-${c.id}`}
                                                    />
                                                </div>
                                                <SignaturePad
                                                    onSubmit={(dataUrl) => sign(c.id, dataUrl)}
                                                    disabled={isLocked}
                                                />
                                                <button
                                                    onClick={() => setActivePad(null)}
                                                    className="text-xs text-slate-500 hover:text-[#0B192C] font-semibold"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setActivePad(c.id)}
                                                disabled={isLocked}
                                                className="btn-gold w-full text-sm disabled:opacity-40"
                                                data-testid={`open-signature-${c.id}`}
                                            >
                                                <FileSignature className="inline -mt-1 mr-1" size={14} />
                                                {isLocked ? "Signature bloquée" : "Signer avec ma souris"}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {c.status === "signed" && c.signed_at && (
                                    <div className="text-xs text-slate-500 border-t border-slate-100 pt-2 mt-2">
                                        Signé le {new Date(c.signed_at).toLocaleDateString("fr-FR")} par <b>{c.signed_by_name}</b>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
